import { PayloadAction, createSlice } from '@reduxjs/toolkit'

type ChatUxState = {
  settingsLastFetched: number | null
  settingsFetchIsLoading: boolean
  defaultJurisdiction: string | null
  researchContactPromptCompleted: boolean
  customInstructions: string
  activeConversationId: string | null
  /** The conversation whose chat is on screen; null while a draft hides it or no chat is open. */
  visibleChatConversationId: string | null
}

const initialState: ChatUxState = {
  settingsLastFetched: null,
  settingsFetchIsLoading: false,
  defaultJurisdiction: null,
  customInstructions: '',
  researchContactPromptCompleted: false,
  activeConversationId: null,
  visibleChatConversationId: null,
}

/**
 * Chat UX Slice
 * A slice to hold state for purely UX state that should not be lost when components are unmounted
 */
export const chatUxSlice = createSlice({
  name: 'chatUxState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    setSettingsLastFetched: (state, action: PayloadAction<{ lastFetched: number }>) => {
      const { lastFetched } = action.payload
      state.settingsLastFetched = lastFetched
    },

    setSettingsFetchIsLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      const { isLoading } = action.payload
      state.settingsFetchIsLoading = isLoading
    },

    setDefaultJurisdiction: (state, action: PayloadAction<{ jurisdiction: string | null }>) => {
      state.defaultJurisdiction = action.payload.jurisdiction
    },

    setCustomInstructions: (state, action: PayloadAction<{ customInstructions: string }>) => {
      state.customInstructions = action.payload.customInstructions
    },

    setResearchContactPromptCompleted: (state, action: PayloadAction<boolean>) => {
      state.researchContactPromptCompleted = action.payload
    },

    setActiveConversationId: (state, action: PayloadAction<{ conversationId: string | null }>) => {
      const { conversationId } = action.payload
      state.activeConversationId = conversationId
    },

    setVisibleChatConversationId: (state, action: PayloadAction<{ conversationId: string | null }>) => {
      state.visibleChatConversationId = action.payload.conversationId
    },
  },
})

// Actions
export const { nullifyData, setSettingsLastFetched, setSettingsFetchIsLoading, setDefaultJurisdiction, setActiveConversationId, setVisibleChatConversationId } =
  chatUxSlice.actions

export default chatUxSlice.reducer
